import * as React from 'react';
import {Point} from 'bezier-js';

import {emotionStyled, styledDiv, styled} from '@packages/utilities/styled';
import {VisuallyHidden} from '@packages/systems/spring';
import {translate} from '@packages/utilities/inline-style-utils';

import {
  getKnobPosition,
  dragStart,
  drag,
  dragEnd,
  KNOB_DIAMETER,
} from '../logic/slider';
import draggable from '../logic/draggable';
import {Locator} from '@packages/systems/ui/overlay';
import type {OnChangeMeta} from '../WebflowUnitInput';
// eslint-disable-next-line webflow/no-dangerous-imports
import DragGhost from '@packages/systems/ui/shared-ui/DragGhost';
import GenericKnob from '../Knob';

const trackThickness = 2;
const knobFixedPosition = (trackThickness - KNOB_DIAMETER) / 2;

const SliderWrapper = draggable(
  styledDiv(({theme, vertical, length, style}) => {
    const widthOrHeight: string = vertical ? 'width' : 'height';
    const heightOrWidth: string = vertical ? 'height' : 'width';
    return {
      display: 'flex',
      flexDirection: vertical ? 'row' : 'column',
      justifyContent: 'center',
      [widthOrHeight]: theme.control.height,
      [heightOrWidth]: length,
      ...style,
    };
  })
);

const Track = styledDiv(
  ({theme}) => ({
    position: 'relative',
    flexBasis: trackThickness,
    borderRadius: theme.control.borderRadius,
    backgroundColor: theme.Slider.background,
  }),
  {
    disabled: {
      boxShadow: 'none',
      opacity: 0.3,
    },
  }
);

const TrackProgressView = emotionStyled('div')({
  pointerEvents: 'none',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  borderRadius: 'inherit',
});

const Knob = styled(GenericKnob)(
  ({displacement, knobRadius, knobSize}) => ({
    position: 'absolute',
    width: knobSize ?? KNOB_DIAMETER,
    height: KNOB_DIAMETER,
    top: knobFixedPosition,
    left: 0,
    transform: translate(displacement),
    cursor: 'ew-resize',
    borderRadius: knobRadius ?? 9999,
  }),
  {
    vertical: ({displacement}) => ({
      top: 0,
      left: knobFixedPosition,
      transform: translate(0, displacement),
      cursor: 'ns-resize',
    }),
  }
);

const changeType = {changeType: 'change'} as const;
const changeStartType = {changeType: 'changeStart'} as const;
const changeEndType = {changeType: 'changeEnd'} as const;

type WebflowSliderProps = {
  style?: React.CSSProperties;
  ['data-automation-id']?: string;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  value: number;
  knobRadius?: number;
  knobSize?: number;
  onChange: (value: number, meta: OnChangeMeta) => void;
  ['aria-label']?: string;
  length?: number;
};

type WebflowSliderState = {
  value: number;
  dragging: boolean;
  reverse: boolean;
  vertical: boolean;
};

class Slider extends React.PureComponent<
  WebflowSliderProps,
  WebflowSliderState
> {
  state = {
    value: this.props.value || 0,
    vertical: false,
    reverse: false,
    dragging: false,
  };

  componentDidUpdate(prevProps: WebflowSliderProps) {
    if (
      prevProps.value != null &&
      this.props.value == null &&
      this.state.value !== this.props.min
    ) {
      this.setState({value: this.props.min});
    }
  }

  getValueForRender = () =>
    this.props.value != null ? this.props.value : this.state.value;

  getConfigFromProps = () => {
    const {step, min, max, length, knobSize} = this.props;
    return {step, min, max, length, knobSize};
  };

  onInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return;
    }

    const value = parseFloat(event.currentTarget.value);
    this.setState(
      (state) => {
        const nextState = {...state, value};
        return nextState;
      },
      this.props.onChange
        ? () => {
            this.props.onChange(this.state.value, changeType);
          }
        : undefined
    );
  };

  onDragStart = (coords: Point) => {
    if (this.props.disabled) {
      return;
    }

    this.setState(
      (state) => dragStart({...state, ...this.getConfigFromProps()}, coords),
      this.props.onChange
        ? () => {
            this.props.onChange(this.state.value, changeStartType);
          }
        : undefined
    );
  };

  onDrag = (coords: Point) => {
    if (this.props.disabled) {
      return;
    }

    this.setState(
      (state) => {
        const nextState = drag(
          {...state, ...this.getConfigFromProps()},
          coords
        );
        return nextState;
      },
      this.props.onChange
        ? () => {
            this.props.onChange(this.state.value, changeType);
          }
        : undefined
    );
  };

  onDragEnd = () => {
    if (this.props.disabled) {
      return;
    }

    this.setState(
      (state) => dragEnd({...state, ...this.getConfigFromProps()}),
      this.props.onChange
        ? () => {
            this.props.onChange(this.state.value, changeEndType);
          }
        : undefined
    );
  };

  render() {
    const {
      step,
      min,
      max,
      disabled,
      style,
      'aria-label': ariaLabel,
      'data-automation-id': dataAutomationId,
      knobRadius,
      knobSize,
    } = this.props;
    const {dragging, vertical} = this.state;
    const value = this.getValueForRender();
    const displacement =
      // @ts-expect-error - TS2345 - Argument of type '{ step: number; min: number; max: number; length: number | undefined; knobSize: number | undefined; value: number; }' is not assignable to parameter of type '{ min: any; max: any; value: any; length: any; reverse: any; knobSize: any; }'.
      getKnobPosition({
        value,
        ...this.getConfigFromProps(),
      }) || 0;

    const knobAutomationId = dataAutomationId ? `${dataAutomationId}-knob` : '';

    return (
      <SliderWrapper
        dragging={dragging}
        // Used in draggable logic
        mode="relative"
        onDrag={this.onDrag}
        onDragEnd={this.onDragEnd}
        onDragStart={this.onDragStart}
        style={style}
        vertical={vertical}
        data-legacy-ds="WebflowSlider"
      >
        <VisuallyHidden
          as="input"
          aria-label={ariaLabel}
          aria-orientation={vertical ? 'vertical' : 'horizontal'}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={value}
          data-automation-id={dataAutomationId}
          // @ts-expect-error TS2322 - This is a valid value for HTMLInputElement.
          max={max}
          min={min}
          onChange={this.onInputChange}
          step={step}
          type="range"
          value={value}
        />
        <DragGhost
          dragging={dragging}
          cursor={vertical ? 'ns-resize' : 'ew-resize'}
        />
        <Track disabled={disabled}>
          <TrackProgressView
            role="presentation"
            style={{
              width: vertical ? '100%' : displacement,
              height: vertical ? displacement : '100%',
            }}
          />
          <Knob
            data-automation-id={knobAutomationId}
            displacement={displacement}
            knobRadius={knobRadius}
            knobSize={knobSize}
          />
        </Track>
      </SliderWrapper>
    );
  }
}

export default (props: WebflowSliderProps) => {
  const knobSize = 4;
  const knobRadius = 1;
  return (
    <Locator>
      {(ref, anchor) => (
        <div ref={ref} style={props.style}>
          <Slider
            length={anchor?.width}
            {...props}
            knobSize={knobSize}
            knobRadius={knobRadius}
          />
        </div>
      )}
    </Locator>
  );
};
